import { AutocompleteSelect, AutocompleteSelectProps, Button, ButtonVariants, TBaseItem } from "@centricsoftware/design-system";
import { AutocompleteChangeDetails, AutocompleteChangeReason } from "@mui/material";
import { CLEAR_RES, GO_TO_DIALOG_RES, OPEN_RES } from "csi/components/constants";
import { StoreNames } from "csi/store/Names";
import { useLocalStoreWithDep } from "designSystem/hooks";
import { observer } from "mobx-react-lite";
import React, { FC, SyntheticEvent } from "react";
import { GoToDialogSave, OnLoadOptions } from "./store/SelectStoreBase";
import { SingleSelectStore } from "./store/SingleSelectStore";

export interface SingleSelectProps
	extends Omit<
		AutocompleteSelectProps<TBaseItem>,
		"clearButtonAriaLabel" |
		"dropdownButtonAriaLabel" |
		"noOptionsLabel" |
		"options" |
		"value"
	> {
	/** store names which includes the aggregate store */
	aggregateStore: StoreNames;

	/** value to display ref related attributes */
	options?: TBaseItem[];

	/** enum form data */
	enumFormData?: FormData;

	/** on options callback */
	onLoadOptions?: OnLoadOptions;

	/** go to dialog save callback */
	goToDialogSave?: GoToDialogSave;

	/** value of the select */
	value?: string;
}

const SingleSelect: FC<SingleSelectProps> = observer(({
	aggregateStore,
	error,
	errorMessage,
	goToDialogSave,
	label,
	onChange,
	onLoadOptions,
	options: displayOptions,
	enumFormData,
	value,
	...otherProps
}) => {
	const singleSelectStore = useLocalStoreWithDep(
		() => new SingleSelectStore(aggregateStore, onLoadOptions, label, goToDialogSave, enumFormData, displayOptions, value),
		[aggregateStore, displayOptions, value]);

	if (!singleSelectStore) {
		return;
	}

	const { canGoToDialog, errorText, isLoading, onListActionClick, onOpen, options, updateSingleSelectValue } = singleSelectStore;
	const isError = error || !!errorMessage || !!errorText;

	// update the single select value, call onChange
	const onChangeCallback = (event: SyntheticEvent<Element, Event>, value: TBaseItem, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<TBaseItem>): void => {
		const newValue: string = value?.id || "";
		updateSingleSelectValue(newValue);
		onChange?.(event, value, reason);
	};

	return (
		<AutocompleteSelect<TBaseItem>
			clearButtonAriaLabelRes={CLEAR_RES}
			dropdownButtonAriaLabelRes={OPEN_RES}
			error={isError}
			errorMessage={errorMessage || errorText}
			isLoading={isLoading}
			label={label}
			listAction={canGoToDialog && (
				<Button
					childrenRes={GO_TO_DIALOG_RES}
					variant={ButtonVariants.link}
				/>
			)}
			listActionDisabled={isError}
			onChange={onChangeCallback}
			onListActionClick={onListActionClick}
			onOpen={onOpen}
			options={options}
			value={value}
			{...otherProps}
		/>
	);
});

export default SingleSelect;